import { CCity, c_cities } from '../mdl/c-city';
import { CPlan } from '../mdl/c-plan';
import { CProducer } from '../mdl/c-producer';
import { CLoginUser, cUserAnalyticsProfile } from '../mdl/c-user';
import { CVenueSceneView } from '../mdl/c-venue';
import { LScope } from '../mdl/l-scope';
import { PPromo } from '../mdl/p-promo';
import { TLIdentityProvider } from '../mdl/zzz-legacy-user';
import { $recaptcha } from '../ui/media/recaptcha';
import { $analytics } from '../y/analytics';
import { $api } from '../y/api';
import { setXavisExperiments, yAddCrashCallback } from '../y/debug';
import { $fn } from '../y/fn';
import { $http } from '../y/http';
import { the_language, ySetLanguage } from '../y/i18n';
import { $storage } from '../y/storage';
import { YCountryId, YId, YObjectId } from '../y/types';

export let the_user: CLoginUser | undefined;
export let the_alt_token: string | undefined;
export let the_login_scope: LScope | undefined;
export let the_login_token: string | undefined;
export let the_reset_token: string | undefined;
export let the_settings: TLLoginResponseSettings | undefined;
export let the_user_is_new: boolean | undefined;
export let the_intercom_user_hash: string | undefined;

interface TLStats {
    active_sessions_city: { [city: YId]: number };
    active_sessions: number;
    all_sessions: number;
    active_events: number;
    all_events: number;
    confirmed_bookings: number;
    cities: number;
    venues: number;
}

declare global {
    interface Window {
        y_default_country: YCountryId;
        ab_plans: CPlan[];
        ab_stats: TLStats;
    }
}

interface TLLoginModel {
    token: string;
    alt_token: string;
    user: YObjectId;
    scope: LScope;
}

export interface TLIdentityInfo {
    exists: boolean;
    avatar: string;
    first_name: string;
    last_name: string;
    email: string;
    provider: TLIdentityProvider;
    token: string;
}

interface TLLoginResponseSettings {
    loyalty_months: number;
    loyalty_days: number;
}

interface TLLoginResponse {
    login: TLLoginModel;
    new_user: CLoginUser;
    identity?: TLIdentityInfo;
    intercom_user_hash?: string;

    settings: TLLoginResponseSettings;
    new_cities: CCity[];
    venues?: CVenueSceneView[];
    producers?: CProducer[];
    keywords?: string[];
    promos?: PPromo[];

    signup: boolean;
}

export interface MELoginEnvironment {
    codes?: string[];
    role_invite?: string;
    verified_phone?: string;
    phone_token?: string;
}

interface TLPreSignupResponse {
    phone_exists: boolean;
    verified_phone_exists: boolean;
    email_exists: boolean;
    test_mode: boolean;
}

type LoginParser = (response: TLLoginResponse) => void;

const login_parsers: LoginParser[] = [];

export function registerLoginParser(parser: LoginParser) {
    login_parsers.push(parser);
}

function parseLoginResponse(data: TLLoginResponse, auto?: boolean) {
    the_login_scope = data.login.scope;

    the_login_token = data.login.token;
    $http.addGlobalHeader('X-Abundo-Token', the_login_token);

    the_user = data.new_user;
    the_login_token = data.login.token;
    the_alt_token = data.login.alt_token;
    the_settings = data.settings;
    the_user_is_new = data.signup;
    the_intercom_user_hash = data.intercom_user_hash;

    if(data.new_user.email === 'gratal@gmail.com') {
        setXavisExperiments(true);
        localStorage.setItem('xavis_experiments', 'true');
    }

    $storage.putString('abundo_access_token_' + the_login_scope, the_login_token, !auto);

    $storage.preference_save_hook = prefs => {
        if(the_user)
            $api.PUT('me/preferences', prefs);
    }
    $storage.loadPreferences(the_user.preferences);

    //parse response
    c_cities.init(data.new_cities);

    for(const p of login_parsers) {
        p(data);
    }

    $analytics.identify(the_user.analytics_id, cUserAnalyticsProfile(the_user), undefined, undefined, undefined, data.signup, the_intercom_user_hash);
    ySetLanguage(the_user.language);
}

export function mePreSignup(scope: LScope, email: string, phone: string, identity_provider: string, identity_token: string) {
    return $http.POST<TLPreSignupResponse>('/api/v1/pre_signup', { scope, email, phone, identity_provider, identity_token }).catch($api.errorHandler);
}

export function meSignup(data: any, scope: LScope, environment: MELoginEnvironment) {
    data.scope = scope;
    data.environment = environment;
    data.language = the_language;
    if(!data.country)
        data.country = window.y_default_country;

    return $recaptcha.execute('signup').then(token => {
        data.recaptcha = token;
        return $http.POST<TLLoginResponse>('/api/v1/signup', data).then(parseLoginResponse, $api.errorHandler);
    });
}

function doLogin(params: any, auto: boolean) {
    return $http.POST<TLLoginResponse>('/api/v1/login', params).then(response => {
        if(response.login)
            parseLoginResponse(response, auto);
        return response;
    }, $api.errorHandler);
}

export function meAutoLogin(scope: LScope, environment: MELoginEnvironment, as_user?: string, reset?: string) {
    if(reset) {
        the_reset_token = reset;
        return Promise.resolve(false);
    }

    const params: any = { scope: scope, environment: environment }

    if(as_user) {
        params.as_user = as_user;
    } else {
        params.token = $storage.getString('abundo_access_token_' + scope);
        if(!params.token)
            return Promise.resolve(false);
    }

    return doLogin(params, true).then($fn.TRUE, () => {
        $storage.remove('abundo_access_token_' + scope);
        return false;
    });
}

export function meEmailLogin(email: string, password: string, scope: LScope, environment: MELoginEnvironment) {
    return doLogin({ email, password, scope, environment }, false);
}

export function meIdentityLogin(provider: TLIdentityProvider, token: string, scope: LScope, environment: MELoginEnvironment) {
    return doLogin({ identity_provider: provider, identity_token: token, scope, environment }, false).then(response => response.identity);
}

export function meEmailTokenLogin(email: string, token: string, scope: LScope, environment: MELoginEnvironment) {
    return doLogin({ email, identity_provider: 'email', identity_token: token, scope, environment }, false);
}

export function mePhoneTokenLogin(phone: string, token: string, scope: LScope, environment: MELoginEnvironment) {
    return doLogin({ phone, identity_provider: 'phone', identity_token: token, scope, environment }, false);
}

export function meLogout() {
    if(the_login_scope) {
        $analytics.track('User Logout');
        $storage.remove('abundo_access_token_' + the_login_scope);
        $storage.discardPreferences();
        $http.POST('/api/v1/logout');
        $http.removeGlobalHeader('X-Abundo-Token');
        the_user = undefined;
        the_login_scope = undefined;
        the_login_token = undefined;
        the_alt_token = undefined;
        the_intercom_user_hash = undefined;
    }
}

export function meForgotPassword(scope: LScope, email: string) {
    return $http.POST('/api/v1/forgot_password', { scope: scope, email: email }).catch($api.errorHandler);
}

export function meResetPassword(_scope: LScope, password: string) {
    return $http.POST<TLLoginResponse>('/api/v1/reset_password', { password: password, token: the_reset_token }).then(response => {
        the_reset_token = undefined;
        return parseLoginResponse(response, false);
    }, $api.errorHandler);
}

yAddCrashCallback((info) => {
    $http.POST('/api/v1/web_log', info);
});
